import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  ViewChildren,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Router } from "@angular/router";
import { LanguageDropdownComponent, LinkComponent } from "@components";
import { socialLink } from "@constants";
import { environment } from "@env";
import { Nullable } from "@models";
import { TranslocoModule } from "@ngneat/transloco";
import { AuthService, EnvironmentService, FuturesModeService, TranslateService } from "@services";
import { map, Observable, take, tap } from "rxjs";
import { DeviceService } from "src/app/core/service/device.service";
import { ThemeService } from "src/app/core/service/theme-switcher.service";
import { ThemeSwitcherComponent } from "src/app/shared/components/theme-switcher/theme-switcher.component";
import { AnimationService } from "../../services/services/animation.service";
@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoModule,
    LinkComponent,
    LanguageDropdownComponent,
    CommonModule,
    ThemeSwitcherComponent,
  ],
})
export class HeaderComponent implements OnInit, AfterViewInit {
  @Input() public isInfoBannerDismissed: boolean = false;
  @Input() public isSignInPage: boolean = false;
  @Output() public bannerDismissed = new EventEmitter<void>();
  @ViewChildren("animatedDiv") public animatedDivs!: QueryList<ElementRef>;

  public hoveredItem: string = "";
  public isDev: boolean = !environment.production;
  public isLoggedIn: boolean = false;
  public isLoggedInLoader: boolean = true;
  public isMobile$ = this._deviceService.isBelow(768);
  public landingPageUrl: string = environment.landingPageUrl;
  public loadingButton: Nullable<string> = null;
  public loginLoader: boolean = false;
  public menuVisible: boolean = false;
  public newChallengeLoader: boolean = false;
  public scrolled: boolean = true;
  public showNavMenu: boolean = true;
  public social = socialLink;
  public isLightTheme$ = this._themeService.isLightTheme$;
  public assetUrl$: Observable<string>;

  constructor(
    private _renderer: Renderer2,
    private _router: Router,
    private _ngZone: NgZone,
    private _deviceService: DeviceService,
    private _animationService: AnimationService,
    private _environmentService: EnvironmentService,
    public translateService: TranslateService,
    private _themeService: ThemeService,
    private _authService: AuthService,
    private _futuresModeService: FuturesModeService,
  ) {
    this._updateHreflang();
  }

  public ngOnInit(): void {
    this._setAssetUrl();
  }

  public ngAfterViewInit(): void {
    this.animatedDivs.forEach((div) => {
      this._animationService.observeElement(div);
    });
  }

  public notifyBannerDismissal(): void {
    this.bannerDismissed.emit();
  }

  private _setAssetUrl(): void {
    this.assetUrl$ = this._themeService.isLightTheme$.pipe(
      map((isLight) => {
        let filename: string = "logo-full";
        if (isLight) filename += "-light";
        return `assets/images/${filename}.svg`;
      }),
    );
  }

  @HostListener("window:scroll", [])
  public onWindowScroll(): void {
    this._ngZone.runOutsideAngular(() => {
      const header: HTMLElement | null = document.getElementById("header");

      if (header) {
        const scrollTop: number =
          window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
        if (scrollTop > 0) {
          this._renderer.addClass(header, "Header_header_scroll");
          this.scrolled = false;
        } else {
          this._renderer.removeClass(header, "Header_header_scroll");
          this.scrolled = true;
        }
      }
    });
  }

  public checkRoute(url: string): void {
    const excludedRoutes: string[] = [
      "/new-challenge",
      "/en/new-challenge",
      "/es/new-challenge",
      "/tr/new-challenge",
      "/order-details-user-information",
      "/en/order-details-user-information",
      "/es/order-details-user-information",
      "/tr/order-details-user-information",
    ];

    this.showNavMenu = !excludedRoutes.includes(url);
  }

  public onMouseEnter(item: string): void {
    this.hoveredItem = item;
  }

  public onMouseLeave(): void {
    this.hoveredItem = "";
  }

  public scrollToSection(elementId: string): void {
    if (window.location.pathname !== "/") {
      this._router.navigate(["/"], { queryParams: { navigateScroll: elementId } });
    } else {
      const element: HTMLElement | null = document.getElementById(elementId);
      if (element) {
        const offset: number = elementId === "how-it-works" ? 100 : 0;
        this._scrollIntoViewWithOffset(element, offset);
      }
    }
  }

  private _scrollIntoViewWithOffset(element: HTMLElement, offset: number): void {
    const elementPosition: number = element.getBoundingClientRect().top + window.scrollY;
    const scrollPosition: number = elementPosition - offset;
    window.scrollTo({ top: scrollPosition, behavior: "smooth" });
  }

  public toggleMenu(): void {
    this.menuVisible = !this.menuVisible;
  }

  public openExternalFaq(): void {
    window.open("https://help.brightfunded.com/en/", "_blank");
  }

  public navigateToUrl(): void {
    this._authService
      .isAuthenticated()
      .pipe(
        take(2),
        tap((isAuthenticated) => {
          if (isAuthenticated) {
            const url: string = this._futuresModeService.isFuturesRoute()
              ? "/futures/dashboard"
              : "/dashboard";
            this._router.navigate([url]);
          } else {
            window.location.href = this.landingPageUrl;
          }
        }),
      )
      .subscribe();
  }

  private _updateHreflang(): void {
    const language: string = this.translateService.getSelectedLanguage();
    const link: HTMLLinkElement = this._renderer.createElement("link");
    link.setAttribute("rel", "alternate");
    link.setAttribute("hreflang", language);
    link.setAttribute("href", `${this._environmentService.getHostname()}/${language}`);

    const existingLink: Element | null = document.querySelector("link[rel='alternate']");
    if (existingLink) {
      existingLink.remove();
    }

    this._renderer.appendChild(document.head, link);
  }
}
